import React, { useEffect, useRef, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AppFormDrawer, AppNote } from "@components"
import { dsColorNew } from "@palette"
import {
	isDsRepricingDrawerOpen, dsRepricingDrawerData, dsRepricingDrawerErrors, clearDsRepricingSignals,
} from "Pages/DsRepricing/Page/@signals"
import DsRepricingDrawerFooter from "Pages/DsRepricing/Page/@components/Footer"
import DsRepricingName from "Pages/DsRepricing/Page/@components/Name"
import DsRepricingPriority from "Pages/DsRepricing/Page/@components/Priority"
import DsRepricingPeriod from "Pages/DsRepricing/Page/@components/Period"
import DsRepricingDurations from "Pages/DsRepricing/Page/@components/DurationRange"
import DsRepricingStrategy from "Pages/DsRepricing/Page/@components/Strategy"
import DsRepricingUndercut from "Pages/DsRepricing/Page/@components/Undercut"
import DsRepricingCompetitorRule from "Pages/DsRepricing/Page/@components/CompetitorRule"
import DsRepricingTethers  from "Pages/DsRepricing/Page/@components/Tethers"
import DsRepricingKpiConditions from "Pages/DsRepricing/Page/@components/KpiConditions"
import DsRepricingExcludeOffices from "Pages/DsRepricing/Page/@components/ExcludeOffices"
import DsRepricingProductSites from "Pages/DsRepricing/Page/@components/ProductSites"
import DsRepricingExcludeWebSuppliers from "Pages/DsRepricing/Page/@components/ExcludeWebSuppliers"
import DsRepricingExcludeProviders from "Pages/DsRepricing/Page/@components/ExcludeProviders"
import DsRepricingFilterProviders from "Pages/DsRepricing/Page/@components/FilterProviders"
import DsRepricingFilterCategories from "Pages/DsRepricing/Page/@components/FilterCategories"
import DsRepricingFilterAcriss from "Pages/DsRepricing/Page/@components/FilterAcriss"
import DsRepricingCompareBy from "Pages/DsRepricing/Page/@components/CompareBy"
import DsRepricingCompareAgainst from "Pages/DsRepricing/Page/@components/CompareAgainst"
import DsRepricingWhichProduct, { useProductState } from "Pages/DsRepricing/Page/@components/WhichProduct"
import DsRepricingWhenItRuns from "Pages/DsRepricing/Page/@components/WhenItRuns"
import { DraftRateShopsProvider } from "Pages/DsRepricing/Page/@hooks/useRateShops"
import DsRepricingStepper from "Pages/DsRepricing/Page/Drawer/@components/Stepper"
import SectionCard from "Pages/DsRepricing/@components/SectionCard"
import { DS_REPRICING_STEPS, stepIsValid, WHEN_IT_RUNS_CARD } from "Pages/DsRepricing/Page/Drawer/steps"

// A compact banner surfaced at the top of the drawer. Lists what used to
// live inside a strategy and now belongs to Setup so the operator does
// not look for the moved knobs inside the wizard.
function MovedToSetupBanner() {
	return (
		<AppNote>
			<Box component="span" sx={{ fontWeight: 600 }}>
				Setup vs strategy:
			</Box>{' '}
			<Box component="span">
				Category consistency, Price limits and Tethering are global
				rules defined in <em>Setup</em> and apply on top of every
				strategy in their scope (each strategy picks its tethers in
				step 5). <em>Products</em> (Setup) say, per site, which
				pricelist each of our offers reads and writes and the
				broker's markup cap. A strategy picks <em>one product</em> and
				decides <em>when it runs</em>, <em>on what</em>, <em>how the
				price is calculated</em> and <em>who it is compared against</em>.
			</Box>
		</AppNote>
	)
}

// A label between two groups of cards, in the stepper's kicker type: the
// scope step holds what the rule prices and what it is compared against,
// and the two have to read as two blocks, not six cards in a column
// (CU-869fe6mf4, UX decision 3). A line fills the rest of the row.
function GroupLabel({ children }) {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
			                  textTransform: 'uppercase', color: dsColorNew.textSubtle,
			                  whiteSpace: 'nowrap' }}>
				{children}
			</Typography>
			<Box sx={{ flex: 1, height: '1px', bgcolor: dsColorNew.grayBorder }}/>
		</Box>
	)
}

// Every panel stays mounted and is hidden with `display`, not unmounted: the field components own
// local state and publish their validation into `dsRepricingDrawerErrors`, so unmounting the steps
// the user has not opened would silently drop both.
function Panel({ step, current, title, description, children }) {
	return (
		<Box sx={{ display: step === current ? 'block' : 'none' }}>
			<Box sx={{ mb: 2.25 }}>
				<Typography sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle, mb: 0.625 }}>{title}</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, maxWidth: 560, lineHeight: 1.5 }}>
					{description}
				</Typography>
			</Box>
			{children}
		</Box>
	)
}

export default function DsRepricingDrawer({ onSubmit, isLoading }) {
	const isEdit = !!dsRepricingDrawerData.peek().id
	const isOpen = isDsRepricingDrawerOpen.value
	const compareByCategory = dsRepricingDrawerData.value?.compareByCategory ?? false
	// Ranking is the only strategy with a target position, so it is the
	// only one the incomplete-market adjustment can reach.
	const isRanking = dsRepricingDrawerData.value?.strategy?.type === 'ranking'
	const [current, setCurrent] = useState(1)
	// A step change has to put the reader back at the top. The steps are
	// long enough to scroll, and "Next" is at the bottom, so advancing
	// left the operator looking at the footer of a step they had not
	// read a word of (Curro, 2026-09-28). Anchoring to the stepper —
	// rather than scrolling some ancestor by hand — works whichever
	// element the drawer actually scrolls.
	const topRef = useRef(null)
	const previousStep = useRef(1)
	useEffect(() => {
		if (previousStep.current === current) return
		previousStep.current = current
		topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
	}, [current])

	// A caller may ask for a step and a card on open (`_openStep`,
	// `_openCard`: the alignment hint and the table send the operator to
	// "When does it run?"). Bookkeeping the API never sees: `toServer`
	// whitelists both out. The scroll waits a tick for the drawer to paint.
	const whenItRunsRef = useRef(null)
	useEffect(() => {
		if (!isOpen) return
		const d = dsRepricingDrawerData.peek()
		setCurrent(d._openStep || 1)
		if (d._openCard === WHEN_IT_RUNS_CARD) {
			setTimeout(() => whenItRunsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 150)
		}
	}, [isOpen])

	const errors = dsRepricingDrawerErrors.value
	// The product's state paints card 2's border red when the held product
	// is invalid (UX D3) — deleted, emptied, or a row that now writes a
	// refused pricelist.
	const tariff = useProductState()
	const invalidSteps = DS_REPRICING_STEPS
		.filter(s => !stepIsValid(s.step, errors))
		.map(s => s.step)

	return <AppFormDrawer
		icon={isEdit ? 'pen' : 'file-plus'}
		title={isEdit ? dsRepricingDrawerData.peek().name : "Create DS Repricing Configuration"}
		width="1120px"
		isOpen={isDsRepricingDrawerOpen.value}
		// No `variant`: DrawerSeeker only forwards `containerProps` to the MUI Drawer, so a variant
		// passed here was silently dropped and the drawer has always been `temporary` — a Modal that
		// UNMOUNTS its content on close. Every block below relies on that: they seed their local state
		// from `dsRepricingDrawerData.peek()` on mount, which is only correct because opening another
		// rule remounts them. Making it persistent would freeze the form on the first rule opened.
		// `anchor` is not passed either: the Seeker hardcodes 'right'.
		// The × in the header is the footer's Cancel, nothing more: the backdrop still does not close it.
		onClose={clearDsRepricingSignals}
		footer={
			<DsRepricingDrawerFooter
				onSubmit={onSubmit}
				isLoading={isLoading}
				current={current}
				onStepChange={setCurrent}
			/>
		}
	>
		{/* One engine count for the draft: When does it run and When does it apply read it (CU-869fkr1a9). */}
		<DraftRateShopsProvider>
		<Box ref={topRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			{/* Orientation, not instruction: it explains the split between
			    Setup and a strategy, which you need once on the way in. On
			    every step it was five lines of the same paragraph pushing
			    the wizard down the screen. */}
			{current === 1 && <MovedToSetupBanner/>}
			{/* The step bar stays in view while the body scrolls (Curro,
			    2026-10-20): sticky against the drawer's scroller, which is
			    DrawerSeeker's DialogContent (`AppFormDrawer` pads it 3 / 2.5,
			    hence the negative margins so the band spans its full width).
			    It takes the body's grey and sits above the cards; the shadow
			    under the card says "pinned". No behaviour change. */}
			<Box sx={{ position: 'sticky', top: 0, zIndex: 3, mx: -3, px: 3, pb: 1, mb: -1,
			           bgcolor: dsColorNew.contentBg }}>
				<Box sx={{ bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
				           borderRadius: '14px', px: 1.5, pb: 1.5,
				           boxShadow: `0 8px 14px -10px ${alpha(dsColorNew.textTitle, 0.28)}` }}>
					<DsRepricingStepper current={current} invalidSteps={invalidSteps} onSelect={setCurrent}/>
				</Box>
			</Box>

			<Box>
				{/* STEP 1 — what the rule is: identity, the product it prices,
				    how often it runs. Which pickups it reprices is a selector
				    and lives in step 2 (Curro, 2026-10-16). */}
				<Panel
					step={1}
					current={current}
					title="Basic"
					description="What this rule is, which of our products it prices and how often it runs."
				>
					<Stack gap={2}>
						<SectionCard step={1} title="Identity"
						             subtitle="How you recognise the rule in the list, and how much weight it carries when two rules cover the same price. The lower number wins — between strategies, and between brokers writing the same tariff.">
							{/* Name grows, Priority stays 120px so both inputs share
							    the same 40px height and align cleanly. */}
							<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
								<Box sx={{ flex: 1 }}><DsRepricingName/></Box>
								<Box sx={{ flex: '0 0 120px' }}><DsRepricingPriority/></Box>
							</Box>
						</SectionCard>

							{/* Card 2 (CU-869fhuhy6, brief v3): the one product this
							    strategy prices. Required like the name, so step 1
							    blocks Next and Save on it with the same marker. The
							    card border goes red when the held product is invalid
							    (D3); SectionCard has no border prop and is shared
							    with Schedule, so the colour is set from outside. */}
							<Box sx={tariff.state === 'invalid' ? { '& > div': { borderColor: dsColorNew.dangerRed } } : undefined}>
								<SectionCard step={2} title="Which product"
								             subtitle="The one product this strategy prices. The product (Setup → Products) says, per site, which pricelist the engine reads, the broker's markup cap and which pricelist it writes. Required: without it the strategy cannot be saved.">
									<DsRepricingWhichProduct/>
								</SectionCard>
							</Box>

							{/* Card 3 (UX D4/D5, brief v3): the cadence, always this
							    strategy's own. The old Scheduler window is gone; this
							    is where it went. The ref is the deep link's landing
							    point. */}
							<Box ref={whenItRunsRef} sx={{ scrollMarginTop: 16 }}>
								<SectionCard step={3} title="When does it run?"
								             subtitle="How often the engine fires for this strategy, and on which days. Which pickup dates it searches, and at which pickup / dropoff time, comes from When does it apply (Dates & LOR, card 1). This strategy's own; a new one starts from the factory default.">
								<DsRepricingWhenItRuns/>
							</SectionCard>
						</Box>
					</Stack>
				</Panel>

				{/* STEP 2 — dates: a selector of what gets repriced (which
				    pickups, how long), not of when the engine runs. */}
				<Panel
					step={2}
					current={current}
					title="Dates & LOR"
					description="Which pickup dates and rental lengths it reprices."
				>
					<Stack gap={2}>
						<SectionCard step={1} title="When does it apply?"
						             subtitle="Which pickup dates the rule covers: a calendar season, or a distance to the pickup. One of the two, narrowed by pickup weekday — these are also the pickup dates the engine searches, at the pickup / dropoff time set here. This is about the rentals priced — when the engine fires is Basic, card 3.">
							<DsRepricingPeriod/>
						</SectionCard>

							<SectionCard step={2} title="Rental lengths"
							             subtitle="The rental lengths this rule prices, in days. A rental no window covers is simply not touched by the rule. Which duration groups are searched comes from the brackets of the product's read pricelists (Basic, card 2).">
							<DsRepricingDurations/>
						</SectionCard>
					</Stack>
				</Panel>

				{/* STEP 3 — scope: which of our own products this rule prices, and
				    which slice of the market it is compared against */}
				<Panel
					step={3}
					current={current}
						title="Scope"
						description="Where this rule prices (offices, vehicles), and which slice of the market its price is compared against — on the sites of its product, and against which kinds of offer."
					>
					{/* The six filters used to ride loose here, six toggles and
					    six selectors with nothing saying what any of them meant
					    or how they combined. That is how the Sites axis went
					    unnoticed as broken: an empty selector looked like a
					    filter nobody had set. Grouped now by the question each
					    one answers, and each group says the rule out loud.

					    Two blocks since CU-869fe6mf4: what the rule prices (our
					    offices, our faces, our vehicles) and what it is compared
					    against (channels, competitors). The unit sits above both
					    because it governs both halves. The face axes live in
					    the first block on purpose: next to "Which competitors"
					    an "Insurance" field reads as "compare only against this
					    insurance", which is exactly what it does not do. */}
					<Stack gap={2}>
						<SectionCard step={1} title="Compare by"
						             subtitle="The unit the whole market is compared on, and the entity the price ladder is built from.">
							<DsRepricingCompareBy/>
							<Typography component="span"
							            sx={{ display: 'block', mt: 0.875, fontSize: 12, color: dsColorNew.textMuted }}>
								By exact ACRISS code, or grouped by vehicle category.
							</Typography>
						</SectionCard>

						<GroupLabel>What this rule prices</GroupLabel>

						<SectionCard step={2} title="Where"
						             subtitle="The offices this rule reaches. A group covers every office inside it. Empty means every office.">
							<DsRepricingExcludeOffices/>
						</SectionCard>

							{/* No "Which of our products" card since brief v3: the
							    product (Basic, card 2) says which of our rows the rule
							    prices, per site. */}
							<SectionCard step={3}
						             title={compareByCategory ? 'Which categories' : 'Which vehicles'}
						             subtitle={compareByCategory
						                 ? 'The vehicle categories this rule prices. Empty means every category.'
						                 : 'The ACRISS codes this rule prices. Empty means every code.'}>
							{compareByCategory
								? <DsRepricingFilterCategories/>
								: <DsRepricingFilterAcriss/>}
						</SectionCard>

						<GroupLabel>What it is compared against</GroupLabel>

							<SectionCard step={4} title="Which channels"
							             subtitle="The sites of the product this rule is compared on (none selected = all of them), and the web suppliers whose prices count. Both apply at once.">
								<Stack gap={2.5}>
									<DsRepricingProductSites/>
								<DsRepricingExcludeWebSuppliers/>
							</Stack>
						</SectionCard>

							<SectionCard step={5} title="Which competitors"
						             subtitle="Tiers are buckets of providers; the second field names them one by one. Both apply, so a rule can say “the premium tier, but only Hertz”.">
							<Stack gap={2.5}>
								<DsRepricingExcludeProviders/>
								<DsRepricingFilterProviders/>
							</Stack>
						</SectionCard>

							{/* Card 6 (CU-869fh646w, brief v3 criterion 12, UX v3 D18): which
							    kinds of offer count as competitors. It decides WHO counts as a
							    competitor, like "Which channels" and "Which competitors" above,
							    so it lives in the market block, not in Pricing strategy. One
							    radio: same kind as my product, every kind, or a specific kind. */}
							<SectionCard step={6} title="Which kinds of offer"
							             subtitle="Which kinds of offer count as competitors. By default, the same kind as the product's own row on each site — like with like. This changes who we compare against, never which of our rows this rule prices.">
								<DsRepricingCompareAgainst/>
							</SectionCard>
					</Stack>
				</Panel>

				{/* STEP 4 — how the target price is produced */}
				<Panel
					step={4}
					current={current}
					title="Pricing strategy"
					description="Choose how the target price is calculated and adjust its parameters."
				>
					{/* The three blocks are the order the engine applies them:
					    the strategy produces a reference, the undercut moves
					    the bid off it, the guard refuses to let it land
					    somewhere the brand should not be seen. Numbering them
					    says that without a paragraph. */}
					<Stack gap={2}>
						{/* The second sentence is only true of Ranking, and it is
						    the one thing about this rule a revenue manager cannot
						    find out here otherwise: since CU-869fbfnuy the engine
						    prices ABOVE the dearest competitor when the target
						    position is empty, which is the opposite direction to
						    the undercut configured below. Said before the surprise
						    rather than after it (UX gate D3, 2026-10-05).

						    The 5 % is written out here, and this is the one place in
						    the product that does not read it from the event: Activity
						    paints `uplift_pct` off the payload and would follow a
						    change on its own, leaving this sentence lying in silence.
						    It lives in `pms-adapter/src/repricing/strategies/ranking.py`
						    as `INCOMPLETE_MARKET_UPLIFT_PCT`; whoever moves it should
						    land on this comment by grepping for that name (TL, v2.2). */}
						<SectionCard step={1} title="Reference price"
						             subtitle={'What the bid is measured against: the average of the market, '
						                 + 'or the competitor sitting at a chosen position.'
						                 + (isRanking
						                     ? ' If that position is empty because there are fewer competitors '
						                       + 'than it asks for, the engine prices 5 % above the dearest '
						                       + 'competitor in scope instead. This is fixed and cannot be configured.'
						                     : '')}>
							<DsRepricingStrategy/>
						</SectionCard>

						{/* Both modes carry an undercut (criterion change,
						    2026-09-19). Market Trend produces an average and the
						    offset moves the bid off it in either direction;
						    Ranking produces a competitor's price and the offset
						    can only go down. */}
						<SectionCard step={2} title="Undercut"
						             subtitle="How far the bid sits below — or above — that reference. Optional with Market Trend: leave it empty to sit exactly on the average.">
							<DsRepricingUndercut/>
						</SectionCard>

						{/* Also both modes, and for the same reason: each
						    strategy produces a price, and either can land
						    somewhere the operator does not want to be seen. */}
						<SectionCard step={3} title="Competitor guard"
						             subtitle="A floor and a ceiling relative to named competitors, tracking whatever they price today. Applied after the undercut, so it always has the last word.">
							<DsRepricingCompetitorRule/>
						</SectionCard>

					</Stack>
				</Panel>

				{/* STEP 5 — which tethers apply to this strategy */}
				<Panel
					step={5}
					current={current}
					title="Tethering"
					description="Pick which tethering rules from Setup should apply to this strategy. A tether not selected here is invisible to the engine for this rule."
				>
					<SectionCard step={1} title="Tethers this strategy uses"
					             subtitle="Tethering rules are defined once in Setup; each strategy picks which of them apply. One not selected here is invisible to the engine for this rule.">
						<DsRepricingTethers/>
					</SectionCard>
				</Panel>

				{/* STEP 6 — PMS-driven activation gate (optional) */}
				<Panel
					step={6}
					current={current}
					title="My Business"
					description="Optional. Gate this strategy on PMS metrics — fleet availability, pace, forecast… The engine only runs it when every condition below is true at rate-shop time. Empty list = no gate."
				>
					<SectionCard step={1} title="Activation conditions"
					             subtitle="Every condition must be true at rate-shop time for the strategy to run at all. An empty list is no gate, which is the default.">
						<DsRepricingKpiConditions/>
					</SectionCard>
				</Panel>
			</Box>
		</Box>
		</DraftRateShopsProvider>
	</AppFormDrawer>
}